import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  Sparkles,
  Ticket,
  BookOpen,
  ShieldCheck,
  Zap,
  Clock,
  Users,
  TrendingUp,
  Check,
  Bot,
  MessageSquare,
  Building2,
  Wrench,
  Headset,
} from "lucide-react";
import ContactModal from "@/components/ContactModal";

const Logo = ({ inverse = false }) => (
  <div className="flex items-center gap-2">
    <div className={`w-7 h-7 ${inverse ? "bg-white" : "bg-zinc-900"} rounded-md flex items-center justify-center`}>
      <Sparkles className={`w-4 h-4 ${inverse ? "text-zinc-900" : "text-white"}`} strokeWidth={2.5} />
    </div>
    <span className={`font-display font-black text-xl ${inverse ? "text-white" : "text-zinc-900"}`}>NextGen<span className="text-blue-600"> Resolution Experts</span></span>
  </div>
);

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 overflow-x-hidden">
      {/* Header */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all ${
          scrolled ? "bg-white/80 backdrop-blur-xl border-b border-zinc-200" : "bg-transparent"
        }`}
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-600">
            <a href="#features" className="hover:text-zinc-900 transition" data-testid="nav-features">Features</a>
            <a href="#how" className="hover:text-zinc-900 transition" data-testid="nav-how">How it works</a>
            <a href="#testimonials" className="hover:text-zinc-900 transition" data-testid="nav-testimonials">Testimonials</a>
            <a href="#pricing" className="hover:text-zinc-900 transition" data-testid="nav-pricing">Enterprise</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-zinc-700 hover:text-zinc-900 px-3 py-2" data-testid="header-login">
              Sign in
            </Link>
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-1.5 bg-zinc-900 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-zinc-800 transition-all hover:-translate-y-0.5"
              data-testid="header-cta"
            >
              Talk to sales <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-32 pb-20 sm:pt-40 sm:pb-32 px-6 lg:px-8 relative" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide mb-6" data-testid="hero-badge">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" />
                AI-POWERED · LIVE IN 60 SECONDS
              </div>
              <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-zinc-900">
                IT support that
                <br />
                <span className="text-blue-600">actually feels</span>
                <br />
                instant.
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-zinc-500 max-w-xl leading-relaxed font-medium">
                NextGen Resolution Experts is the AI-first helpdesk that resolves 70% of employee IT issues in under a minute — no queue, no phone tag, no frustration.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 bg-zinc-900 text-white text-sm font-bold px-7 py-4 rounded-full hover:bg-zinc-800 transition-all hover:-translate-y-1 hover:shadow-xl"
                  data-testid="hero-cta-primary"
                >
                  Try the live demo <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => setContactOpen(true)}
                  className="inline-flex items-center gap-2 border-2 border-zinc-200 text-zinc-900 text-sm font-bold px-7 py-4 rounded-full hover:border-zinc-900 transition-all"
                  data-testid="hero-cta-secondary"
                >
                  Talk to our team
                </button>
              </div>
              <div className="mt-10 flex items-center gap-6 text-xs text-zinc-500 font-medium">
                <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-blue-600" /> White-glove onboarding</div>
                <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-blue-600" /> Enterprise-ready</div>
                <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-blue-600" /> Deploys in days, not months</div>
              </div>
            </div>

            {/* Chat preview card */}
            <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 via-zinc-100 to-zinc-50 rounded-3xl blur-2xl -z-10" />
                <div className="bg-white rounded-3xl border border-zinc-200 shadow-2xl shadow-zinc-200/50 overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 bg-zinc-900 rounded-lg flex items-center justify-center">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-900">NextGen IT Assistant</div>
                        <div className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> Online · ~5s response
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <div className="w-2.5 h-2.5 bg-zinc-200 rounded-full" />
                      <div className="w-2.5 h-2.5 bg-zinc-200 rounded-full" />
                      <div className="w-2.5 h-2.5 bg-zinc-200 rounded-full" />
                    </div>
                  </div>
                  <div className="p-5 space-y-3 bg-zinc-50/40 min-h-[320px]">
                    <div className="flex justify-end">
                      <div className="bg-zinc-900 text-white text-sm px-4 py-2.5 rounded-2xl rounded-br-sm max-w-[80%]">
                        I can't connect to the VPN since this morning 😩
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Bot className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="bg-white border border-zinc-200 text-sm px-4 py-3 rounded-2xl rounded-bl-sm max-w-[80%] text-zinc-700">
                        Got it. Let's fix this in 3 steps:
                        <ol className="mt-2 space-y-1 text-zinc-600">
                          <li><b>1.</b> Quit & relaunch the VPN client</li>
                          <li><b>2.</b> Switch from Wi-Fi to mobile hotspot to test</li>
                          <li><b>3.</b> Try gateway <span className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">EU-WEST-2</span></li>
                        </ol>
                        <div className="mt-2 text-zinc-500 text-xs">Still stuck? I'll create a ticket for you.</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0" />
                      <div className="bg-white border border-zinc-200 px-4 py-3 rounded-2xl rounded-bl-sm">
                        <div className="flex gap-1">
                          <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full dot-1" />
                          <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full dot-2" />
                          <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full dot-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating stat */}
                <div className="absolute -bottom-6 -left-6 bg-white px-5 py-3 rounded-2xl border border-zinc-200 shadow-xl hidden sm:block">
                  <div className="text-3xl font-display font-black text-zinc-900">72%</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">tickets auto-resolved</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-10 border-y border-zinc-200 bg-white" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-bold mb-6">
            Trusted by IT teams at fast-moving companies
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-zinc-300">
            {["Nordstream", "Lumen Co.", "Vertex Labs", "Quanta", "Helix", "Apex"].map((b) => (
              <div key={b} className="font-display text-xl sm:text-2xl font-bold tracking-tighter text-zinc-400">
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES - BENTO */}
      <section id="features" className="py-24 sm:py-32 px-6 lg:px-8" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">What you get</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-zinc-900">
              Everything your IT desk needed.<br />
              <span className="text-zinc-400">Nothing it didn't.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
            {/* Big card - AI */}
            <div className="md:col-span-4 bg-zinc-900 text-white p-10 rounded-3xl relative overflow-hidden group" data-testid="feature-ai">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl" />
              <Bot className="w-10 h-10 mb-6 text-blue-400" strokeWidth={1.5} />
              <h3 className="font-display text-3xl font-bold mb-3">AI assistant that knows IT.</h3>
              <p className="text-zinc-400 max-w-md leading-relaxed">
                Trained on the playbook of senior IT engineers. Answers password resets, VPN issues, printer setup, Outlook sync, slow laptops — instantly, 24/7.
              </p>
              <div className="mt-8 inline-flex items-center gap-2 text-blue-400 text-sm font-semibold">
                Powered by GPT-5.2 <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            <div className="md:col-span-2 bg-white p-8 rounded-3xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid="feature-tickets">
              <Ticket className="w-8 h-8 mb-5 text-zinc-900" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold mb-2 text-zinc-900">Smart tickets</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">Auto-categorized, auto-prioritized, auto-routed. Your IT team focuses on real problems.</p>
            </div>

            <div className="md:col-span-2 bg-white p-8 rounded-3xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid="feature-kb">
              <BookOpen className="w-8 h-8 mb-5 text-zinc-900" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold mb-2 text-zinc-900">Knowledge base</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">Pre-loaded with 50+ articles covering every common workplace IT scenario.</p>
            </div>

            <div className="md:col-span-2 bg-blue-600 text-white p-8 rounded-3xl hover:shadow-xl hover:-translate-y-1 transition-all" data-testid="feature-fast">
              <Zap className="w-8 h-8 mb-5" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold mb-2">~5 second replies</h3>
              <p className="text-sm text-blue-100 leading-relaxed">Streamed token-by-token. No waiting for the email reply that never comes.</p>
            </div>

            <div className="md:col-span-2 bg-white p-8 rounded-3xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid="feature-secure">
              <ShieldCheck className="w-8 h-8 mb-5 text-zinc-900" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold mb-2 text-zinc-900">Secure by default</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">JWT auth, bcrypt hashing, no credentials ever sent to AI. Enterprise-ready.</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 sm:py-32 px-6 lg:px-8 bg-white border-y border-zinc-200" data-testid="how-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">How it works</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-zinc-900">
              Three steps. <br />
              That's the whole pitch.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: "01",
                title: "Ask in plain English",
                desc: "Type your IT problem like you'd text a friend. No forms, no dropdowns, no jargon.",
                icon: MessageSquare,
              },
              {
                step: "02",
                title: "Get the fix in seconds",
                desc: "Our AI walks you through step-by-step instructions — tailored to your OS and software stack.",
                icon: Zap,
              },
              {
                step: "03",
                title: "Escalate only if needed",
                desc: "Still stuck? One click creates a ticket. Your IT team gets the full context — no back-and-forth.",
                icon: Ticket,
              },
            ].map((s, i) => (
              <div
                key={s.step}
                className="bg-zinc-50 p-8 rounded-3xl border border-zinc-100 relative overflow-hidden group hover:bg-white hover:border-zinc-200 hover:shadow-xl transition-all"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="font-mono text-xs font-bold text-zinc-400 mb-6">STEP {s.step}</div>
                <s.icon className="w-8 h-8 text-blue-600 mb-5" strokeWidth={1.5} />
                <h3 className="font-display text-2xl font-bold text-zinc-900 mb-3">{s.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="py-24 sm:py-32 px-6 lg:px-8" data-testid="metrics-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8">
          {[
            { value: "72%", label: "Tickets resolved without human IT" },
            { value: "5s", label: "Average first response time" },
            { value: "$420", label: "Saved per employee, per year" },
            { value: "4.9★", label: "Employee satisfaction rating" },
          ].map((m, i) => (
            <div key={i} className="border-l-2 border-blue-600 pl-6" data-testid={`metric-${i}`}>
              <div className="font-display font-black tracking-tighter text-5xl sm:text-6xl text-zinc-900">{m.value}</div>
              <div className="mt-2 text-sm text-zinc-500 font-medium leading-snug">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 sm:py-32 px-6 lg:px-8 bg-zinc-900 text-white relative overflow-hidden" data-testid="testimonials-section">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-400 mb-3">Why teams switch</div>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mb-16 max-w-3xl">
            "It just works." — every employee, eventually.
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                quote: "Our IT ticket volume dropped 68% in the first month. The team finally has time for strategic work.",
                name: "Sarah Chen",
                role: "Head of IT, Vertex Labs",
                img: "https://images.unsplash.com/photo-1737574821698-862e77f044c1?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
              },
              {
                quote: "I used to dread submitting an IT ticket. Now I just chat — and it actually solves my problem.",
                name: "Marcus Reilly",
                role: "Marketing Lead, Quanta",
                img: "https://images.unsplash.com/photo-1610387694365-19fafcc86d86?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
              },
            ].map((t, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm p-10 rounded-3xl border border-white/10" data-testid={`testimonial-${i}`}>
                <div className="text-2xl font-display font-bold leading-snug mb-8 text-white">"{t.quote}"</div>
                <div className="flex items-center gap-4">
                  <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-bold text-white">{t.name}</div>
                    <div className="text-sm text-zinc-400">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE PRICING */}
      <section id="pricing" className="py-24 sm:py-32 px-6 lg:px-8" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">Enterprise pricing</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mb-4 text-zinc-900">
              Built for your team.<br />
              <span className="text-zinc-400">Priced for your size.</span>
            </h2>
            <p className="text-lg text-zinc-500 max-w-2xl mx-auto">
              Every deployment includes white-glove onboarding, custom knowledge base setup, and a dedicated success engineer.
            </p>
          </div>

          <div className="bg-zinc-900 text-white rounded-3xl p-10 sm:p-14 relative overflow-hidden grid md:grid-cols-2 gap-12 items-center" data-testid="enterprise-plan">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600/10 border border-blue-600/30 text-blue-400 text-[10px] font-bold tracking-widest uppercase mb-6">
                <Building2 className="w-3 h-3" /> Enterprise plan
              </div>
              <div className="font-display font-black tracking-tighter text-5xl sm:text-6xl mb-2">
                Starting at <span className="text-blue-400">$499</span>
                <span className="text-xl font-medium text-zinc-400">/month</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-400 mb-8">
                <Wrench className="w-4 h-4" />
                <span><b className="text-white">$499</b> one-time setup &amp; onboarding fee</span>
              </div>
              <ul className="space-y-3 text-sm text-zinc-200 mb-10">
                {[
                  "Unlimited AI chats & ticket volume",
                  "Custom-trained knowledge base for your company",
                  "Dedicated success engineer & priority Slack support",
                  "Admin dashboard, SSO, SCIM, audit logs",
                  "99.9% uptime SLA with quarterly reviews",
                  "Setup & ongoing management included",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" /> <span>{f}</span></li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                className="inline-flex items-center gap-2 bg-blue-600 text-white font-bold px-7 py-4 rounded-full hover:bg-blue-700 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/25"
                data-testid="enterprise-cta"
              >
                Request Your Custom Setup <ArrowRight className="w-4 h-4" />
              </button>
              <div className="text-xs text-zinc-500 mt-3">Response within 1 business day · No commitment</div>
            </div>

            <div className="relative grid grid-cols-2 gap-4">
              {[
                { icon: Headset, label: "Dedicated success engineer", value: "1:1" },
                { icon: Wrench, label: "Custom setup & migration", value: "Included" },
                { icon: ShieldCheck, label: "Enterprise security (SSO, SCIM)", value: "Built-in" },
                { icon: Zap, label: "Uptime SLA", value: "99.9%" },
              ].map((b, i) => (
                <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
                  <b.icon className="w-5 h-5 text-blue-400 mb-3" strokeWidth={1.8} />
                  <div className="font-display font-bold text-lg text-white leading-none">{b.value}</div>
                  <div className="text-[11px] text-zinc-400 mt-1.5 leading-snug">{b.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-10 text-sm text-zinc-500">
            Already a customer?{" "}
            <Link to="/login" className="font-semibold text-blue-600 hover:underline" data-testid="pricing-signin">
              Sign in to your workspace →
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-200 py-12 px-6 lg:px-8 bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo />
            <div className="flex gap-6 text-sm text-zinc-600">
              <button onClick={() => setContactOpen(true)} className="hover:text-zinc-900" data-testid="footer-contact">Contact sales</button>
              <Link to="/login" className="hover:text-zinc-900" data-testid="footer-signin">Sign in</Link>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-zinc-100">
            <div className="text-sm text-zinc-400">© 2026 NextGen Resolution Experts · Built for teams that ship.</div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-zinc-500">
              <Link to="/terms" className="hover:text-zinc-900" data-testid="footer-terms">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-zinc-900" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/service-agreement" className="hover:text-zinc-900" data-testid="footer-service-agreement">Service Agreement</Link>
              <Link to="/refund-policy" className="hover:text-zinc-900" data-testid="footer-refund-policy">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
